import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Toolbox, MagnifyingGlass, CheckCircle, Wrench, Warning, Prohibit, Tag } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg } from '@/auth/AuthContext';
import { mfgApi, EQUIPMENT_KINDS, EQUIPMENT_STATUSES, EQ_STATUS_BADGE, EQ_KIND_BADGE, WO_STATUS_BADGE } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import { useDepartment } from './DepartmentContext';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildEquipmentLabel } from '@/components/labels/labelSizes';
import EquipmentFormDialog from './EquipmentFormDialog';
import EquipmentDetailDialog from './EquipmentDetailDialog';

function Stat({ Icon, label, value, tone, testid }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${tone} mb-2`}><Icon size={18} weight="duotone" /></div>
      <div className="text-2xl font-bold text-zinc-900 tabular-nums">{value}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

// 2026-10-21 · 台帳はひとつ。部署で絞って見るだけ（技術 | 製造 の切替は廃止）。
const DEPT_CHIPS = [['ALL', 'すべて'], ['SNS', 'SNS'], ['SOL', 'SOL'], ['HTR', 'HTR'],
  ['ENG', 'ENG'], ['CMN', 'CMN']];
// 由来のタグ（登録の種類から）
const ACQ_TAGS = {
  existing: ['既存', 'bg-zinc-100 text-zinc-700 border-zinc-200'],
  purchase: ['購入', 'bg-sky-50 text-sky-700 border-sky-200'],
  in_house: ['自社製作（技術部）', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
};

export default function Equipment({ deptCode = '' }) {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [impact, setImpact] = useState([]);
  const navigate = useNavigate();
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [due, setDue] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editEq, setEditEq] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [labelData, setLabelData] = useState(null);
  const { active } = useDepartment();
  const deptName = active?.name;
  // 部署の絞り込みはこの画面が持つ（技術のメニューから来たら ENG を選んだ状態）
  const [deptFilter, setDeptFilter] = useState(deptCode || '');
  const [acqFilter, setAcqFilter] = useState('all');

  const load = useCallback(() => {
    const params = {};
    if (kind !== 'all') params.kind = kind;
    if (status !== 'all') params.status = status;
    if (q.trim()) params.q = q.trim();
    if (due !== 'all') params.due = due;
    // R2 · ひとつの設備台帳を部署で切り替えて見る（技術 = ENG / すべて = ALL）
    if (deptFilter) params.dept_code = deptFilter;
    if (acqFilter !== 'all') params.acquisition_type = acqFilter;
    mfgApi.equipment(params).then(setList).catch(() => {});
    mfgApi.equipmentStats(deptFilter ? { dept_code: deptFilter } : undefined).then(setStats).catch(() => {});
    mfgApi.equipmentImpact().then((d) => setImpact(Array.isArray(d) ? d : [])).catch(() => {});
  }, [kind, status, q, due, deptFilter, acqFilter]);
  useEffect(() => { const id = setTimeout(load, 200); return () => clearTimeout(id); }, [load]);

  return (
    <div className="space-y-5 page-enter" data-testid="mfg-equipment">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.eq.title')}</h1>
          <p className="mt-0.5 text-xs text-zinc-500" data-testid="equipment-dept-filter">
            設備台帳はひとつです。部署で絞って見ています。
          </p>
        </div>
        {canWrite && (
          <Button data-testid="equipment-new-btn" onClick={() => { setEditEq(null); setFormOpen(true); }} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={18} className="mr-1.5" weight="bold" /> {t('mfg.eq.new')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat Icon={CheckCircle} label={t('mfg.eq.stat.operational')} value={stats?.by_status?.operational ?? 0} tone="bg-emerald-50 text-emerald-600" testid="eq-stat-operational" />
        <Stat Icon={Wrench} label={t('mfg.eq.stat.maintenance')} value={stats?.by_status?.maintenance ?? 0} tone="bg-amber-50 text-amber-600" testid="eq-stat-maintenance" />
        <Stat Icon={Prohibit} label={t('mfg.eq.stat.breakdown')} value={stats?.by_status?.breakdown ?? 0} tone="bg-rose-50 text-rose-600" testid="eq-stat-breakdown" />
        <Stat Icon={Warning} label={t('mfg.eq.stat.overdue')} value={stats?.overdue ?? 0} tone="bg-orange-50 text-orange-600" testid="eq-stat-overdue" />
        <Stat Icon={Wrench} label={t('mfg.eq.stat.openRepairs')} value={stats?.open_corrective ?? 0} tone="bg-violet-50 text-violet-600" testid="eq-stat-openrepairs" />
      </div>

      {/* 生産への影響 — メンテ中・故障の設備を使う未完了オーダー */}
      {impact.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/50 overflow-hidden" data-testid="eq-impact-panel">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-rose-100 flex-wrap">
            <Warning size={16} weight="fill" className="text-rose-500 shrink-0" />
            <span className="text-sm font-semibold text-rose-800">{t('mfg.eq.impact.title')}</span>
            <span className="text-xs text-rose-500/80">{t('mfg.eq.impact.desc')}</span>
          </div>
          <div className="divide-y divide-rose-100/70">
            {impact.map((m) => (
              <div key={m.equipment_id} className="px-4 py-2.5" data-testid={`eq-impact-${m.equipment_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={EQ_STATUS_BADGE[m.status]}>{m.status_label}</Badge>
                  <span className="text-sm font-medium text-zinc-800">{m.name}</span>
                  {m.asset_no && <span className="font-mono text-[11px] text-zinc-400">{m.asset_no}</span>}
                  <span className="text-xs text-zinc-500" data-testid={`eq-impact-count-${m.equipment_id}`}>{t('mfg.eq.impact.count', { n: m.work_orders.length })}</span>
                </div>
                <div className="mt-1.5 grid gap-1 sm:grid-cols-2">
                  {m.work_orders.map((w) => (
                    <button key={`${m.equipment_id}-${w.id}`} type="button" onClick={() => navigate('/mfg/work-orders')}
                      data-testid={`eq-impact-wo-${m.equipment_id}-${w.id}`}
                      className="flex items-center gap-2 rounded-lg border border-rose-100 bg-white px-2.5 py-1.5 text-left hover:border-rose-300 transition-colors">
                      <span className="font-mono text-[11px] text-zinc-500 shrink-0">{w.manufacturing_no || w.wo_no}</span>
                      <span className="truncate text-xs text-zinc-700 flex-1 min-w-0"><span data-no-translate>{w.product_name}</span></span>
                      <span className="text-[11px] text-zinc-400 tabular-nums shrink-0">×{w.qty}</span>
                      <Badge variant="outline" className={`${WO_STATUS_BADGE[w.status] || ''} shrink-0`}>{t(`mfg.wo.status.${w.status}`)}</Badge>
                      <span className={`shrink-0 rounded px-1 py-px text-[9px] font-medium ${w.via === 'direct' ? 'bg-orange-50 text-orange-600' : 'bg-sky-50 text-sky-600'}`}>
                        {w.via === 'direct' ? t('mfg.eq.impact.viaDirect') : t('mfg.eq.impact.viaBom')}
                      </span>
                      {w.due_date && <span className="text-[10px] text-zinc-400 shrink-0 tabular-nums">{w.due_date}</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        {/* 部署で絞る（技術 | 製造 のタブは廃止） */}
        <div className="flex gap-1.5 flex-wrap" data-testid="eq-dept-chips">
          {DEPT_CHIPS.map(([code, label]) => (
            <button key={code} onClick={() => setDeptFilter(code === 'ALL' ? 'ALL' : code)}
              data-testid={`eq-dept-filter-${code}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                (deptFilter || 'ALL') === code
                  ? 'bg-teal-700 text-white border-teal-700'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
              {label}
            </button>
          ))}
        </div>
        {/* 由来で絞る（既存 / 購入 / 自社製作） */}
        <div className="flex gap-1.5 flex-wrap" data-testid="eq-acq-chips">
          {[['all', 'すべての由来'], ['existing', '既存'], ['purchase', '購入'],
            ['in_house', '自社製作']].map(([v, label]) => (
              <button key={v} onClick={() => setAcqFilter(v)} data-testid={`eq-acq-filter-${v}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  acqFilter === v
                    ? 'bg-zinc-800 text-white border-zinc-800'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
                {label}
              </button>
            ))}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['all', ...EQUIPMENT_KINDS].map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`eq-kind-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${kind === k ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
              {k === 'all' ? t('mfg.common.all') : t(`mfg.eq.kind.${k}`)}
            </button>
          ))}
        </div>
        <div className="relative max-w-xs flex-1 min-w-[160px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="equipment-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('mfg.common.search')} className="h-10 pl-9 rounded-lg" />
        </div>
        {/* A6 · 点検・校正の期限で絞り込む */}
        <div className="flex gap-1.5">
          {[['all', t('mfg.common.all')], ['overdue', t('mfg.eq.due.overdue')], ['soon', t('mfg.eq.due.soon')]].map(([v, label]) => (
            <button key={v} onClick={() => setDue(v)} data-testid={`eq-due-filter-${v}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${due === v ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        dataTestId="equipment-table"
        rows={list}
        rowKey={(e) => e.id}
        rowTestId={(e) => `equipment-row-${e.id}`}
        onRowClick={(e) => setDetailId(e.id)}
        empty={{ icon: <Toolbox size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.eq.empty'), testid: 'equipment-empty' }}
        columns={[
          { key: 'asset_no', header: t('mfg.eq.assetNo'), cell: (e) => <span className="font-mono text-xs text-zinc-600">{e.asset_no}</span> },
          { key: 'name', primary: true, header: t('mfg.eq.name'), cell: (e) => <span className="font-medium text-zinc-800">{e.name}</span> },
          { key: 'kind', header: t('mfg.eq.kind'), cell: (e) => <Badge variant="outline" className={EQ_KIND_BADGE[e.kind]}>{e.kind_label}</Badge> },
          { key: 'origin', header: '由来', cell: (e) => {
            const [label, cls] = ACQ_TAGS[e.acquisition_type] || ACQ_TAGS.existing;
            return e.source_development_id ? (
              <a href={`/engineering/developments/${e.source_development_id}`}
                onClick={(ev) => ev.stopPropagation()}
                data-testid={`eq-origin-${e.id}`}
                title="元の開発案件を開く"
                className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold underline ${cls}`}>
                {label}
              </a>
            ) : (
              <Badge variant="outline" className={cls} data-testid={`eq-origin-${e.id}`}>{label}</Badge>
            );
          } },
          { key: 'price', header: '取得価額', cell: (e) => (
            <span className="text-xs tabular-nums text-zinc-700" data-testid={`eq-price-${e.id}`}>
              {e.acquisition_price != null
                ? `¥${Number(e.acquisition_price).toLocaleString()}${e.acquisition_price_from_handover ? '（完了報告書）' : ''}`
                : '—'}
            </span>
          ) },
          { key: 'makermodel', header: `${t('mfg.eq.maker')}/${t('mfg.eq.model')}`, cell: (e) => <span className="text-zinc-600 text-xs">{[e.maker, e.model].filter(Boolean).join(' / ') || '—'}</span> },
          { key: 'location', header: t('mfg.eq.location'), cell: (e) => <span className="text-zinc-600">{e.location_name || '—'}</span> },
          { key: 'inspection', header: t('mfg.eq.inspection.next'), cell: (e) => (
            <div className="flex flex-col gap-0.5 items-start" data-testid={`eq-inspection-cell-${e.id}`}>
              <span className={`text-xs tabular-nums ${e.inspection_overdue ? 'text-rose-600 font-semibold' : (e.inspection_due_soon ? 'text-amber-600 font-semibold' : 'text-zinc-600')}`}>{e.inspection_due_on || '—'}</span>
              {e.inspection_overdue && <Badge variant="outline" data-testid={`eq-inspection-overdue-${e.id}`} className="bg-rose-50 text-rose-700 border-rose-200">{t('mfg.eq.badge.inspectionOverdue')}</Badge>}
              {e.calibration_overdue && <Badge variant="outline" data-testid={`eq-calibration-overdue-${e.id}`} className="bg-rose-50 text-rose-700 border-rose-200">{t('mfg.eq.badge.calibrationOverdue')}</Badge>}
              {!e.inspection_overdue && e.inspection_due_soon && <Badge variant="outline" data-testid={`eq-inspection-soon-${e.id}`} className="bg-amber-50 text-amber-700 border-amber-200">{t('mfg.eq.badge.dueSoon')}</Badge>}
            </div>
          ) },
          { key: 'status', header: '', cell: (e) => <Badge variant="outline" className={EQ_STATUS_BADGE[e.status]}>{e.status_label}</Badge> },
          { key: 'actions', actions: true, header: '', cell: (e) => (<>
            <Button size="sm" variant="ghost" className="h-9 px-2 text-zinc-600" data-testid={`equipment-label-${e.id}`} onClick={() => setLabelData([buildEquipmentLabel(e, deptName)])} title={t('mfg.label.printRow')}><Tag size={16} /></Button>
            <Button size="sm" variant="outline" className="h-9" data-testid={`equipment-view-${e.id}`} onClick={() => setDetailId(e.id)}>{t('mfg.common.edit')}</Button>
          </>) },
        ]}
      />

      <EquipmentFormDialog open={formOpen} equipment={editEq}
        fixedDeptCode={deptFilter && deptFilter !== 'ALL' ? deptFilter : undefined}
        onClose={() => { setFormOpen(false); setEditEq(null); }}
        onSaved={() => { setFormOpen(false); setEditEq(null); load(); }} />
      <EquipmentDetailDialog equipmentId={detailId} onClose={() => setDetailId(null)} onChanged={load}
        onEdit={(e) => { setDetailId(null); setEditEq(e); setFormOpen(true); }} />
      <LabelPrintDialog open={!!labelData} onClose={() => setLabelData(null)} labels={labelData || []} title="設備ラベル" />
    </div>
  );
}
